<script>
import { mapGetters } from 'vuex';
import ConversationHeader from './ConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import S3KanbanPane from '../S3KanbanPane.vue';
import MessagesView from './MessagesView.vue';
import ReturnRefundCard from './ReturnRefundCard.vue';
import PinnedMessagesBar from './PinnedMessagesBar.vue';

export default {
  components: {
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    S3KanbanPane,
    MessagesView,
    ReturnRefundCard,
    PinnedMessagesBar,
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return { activeIndex: 0, kanbanLoaded: false };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      allDashboardApps: 'dashboardApps/getRecords',
      myTeams: 'teams/getMyTeams',
    }),
    // Smith3D staff see their print-job kanban where the "select a
    // conversation" placeholder would be (never on the MakerHub list or My Inbox).
    showsS3Kanban() {
      return (
        !this.isInboxView &&
        this.$route.params.company !== 'mk' &&
        this.myTeams.some(team => team.name.toLowerCase() === 'smith3d')
      );
    },
    // A chat URL is still loading its conversation on first paint — currentChat
    // is empty for a moment, which used to mount (and fetch) the kanban only to
    // hide it again. Only an actual list view shows the board.
    kanbanVisible() {
      return (
        this.showsS3Kanban &&
        !this.currentChat.id &&
        !this.$route.params.conversation_id &&
        !this.$route.params.conversationId
      );
    },
    // Fork: an app whose frame entry lists inbox_ids shows only on those
    // inboxes (e.g. Smith3D apps on the S3 inboxes); apps without it show
    // everywhere, as upstream. Editing an app in Settings drops inbox_ids.
    dashboardApps() {
      const inboxId = this.currentChat.inbox_id;
      return this.allDashboardApps.filter(app =>
        (app.content || []).every(
          item => !item.inbox_ids || item.inbox_ids.includes(inboxId)
        )
      );
    },
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.dashboardApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + 1,
          name: dashboardApp.title,
        })),
      ];
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
  },
  watch: {
    // Load the kanban on first view, then keep it alive so hopping between a
    // chat and the board doesn't reload the JR page.
    kanbanVisible: {
      handler(visible) {
        if (visible) this.kanbanLoaded = true;
      },
      immediate: true,
    },
    'currentChat.inbox_id': {
      immediate: true,
      handler(inboxId) {
        if (inboxId) {
          this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
        }
      },
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col min-w-0 w-full bg-n-surface-1 relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <ConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
      :class="{
        'border-b border-b-n-weak !pt-2': !dashboardApps.length,
      }"
    />
    <woot-tabs
      v-if="dashboardApps.length && currentChat.id"
      :index="activeIndex"
      class="h-10"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :index="tab.index"
        :name="tab.name"
        :show-badge="false"
        is-compact
      />
    </woot-tabs>
    <ReturnRefundCard v-if="currentChat.id" :chat="currentChat" />
    <PinnedMessagesBar v-if="currentChat.id" :chat="currentChat" />
    <div v-show="!activeIndex" class="flex h-full min-h-0 m-0">
      <MessagesView
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-inbox-view="isInboxView"
      />
      <EmptyState
        v-if="!currentChat.id && !isInboxView && !showsS3Kanban"
        :is-on-expanded-layout="isOnExpandedLayout"
      />
      <S3KanbanPane v-if="kanbanLoaded" v-show="kanbanVisible" />
      <slot />
    </div>
    <DashboardAppFrame
      v-for="(dashboardApp, index) in dashboardApps"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 1 === index"
      :config="dashboardApps[index].content"
      :position="index"
      :current-chat="currentChat"
    />
  </div>
</template>
